import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useLiveVideo } from '../hooks/useLiveVideo';
import { useCompletedLives } from '../hooks/useCompletedLives';
import { useUpcomingLives } from '../hooks/useUpcomingLives';
import { useVideoActions } from '../hooks/useVideoActions';
import { useTheme } from '../contexts/ThemeContext';
import { useDirection } from '../contexts/DirectionContext';
import { VideoContent } from '../types/content';
import VideoPlayer from '../components/video/VideoPlayer';
import { VideoDetails } from '../components/video/VideoDetails';
import VideoThumbnail from '../components/video/VideoThumbnail';
import { UpcomingLivesContainer } from '../components/live';
import { MediaListContainer } from '../components/media';
import ErrorBoundary from '../components/common/ErrorBoundary/ErrorBoundary';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { ErrorDisplay } from '../components/common/ErrorBoundary';
import SEO from '../components/common/SEO';

interface LiveStreamPlayerProps {
  video: VideoContent;
  type: 'live' | 'upcoming';
}

const LiveStreamPlayer: React.FC<LiveStreamPlayerProps> = ({ video, type }) => {
  const { handleShare, handleSave, isSaved } = useVideoActions({
    video,
    onShareSuccess: () => {},
    onShareError: (error) => console.error('Share error:', error)
  });

  const handleAddToCalendar = () => {
    if (video.broadcastStatus !== 'upcoming' || !video.scheduledStartTime) return;
    
    const event = {
      title: video.title,
      description: video.description,
      start: video.scheduledStartTime,
      duration: video.duration,
      url: video.url
    };
    window.open(
      `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${
        encodeURIComponent(event.title)
      }&details=${
        encodeURIComponent(event.description)
      }&dates=${
        new Date(event.start).toISOString().replace(/[-:.]/g, '')
      }/${
        new Date(Date.parse(event.start) + event.duration * 1000).toISOString().replace(/[-:.]/g, '')
      }`
    );
  };

  const handleSetReminder = () => {
    if (video.broadcastStatus !== 'upcoming') return;
    if ('Notification' in window) {
      Notification.requestPermission();
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="relative bg-black sm:rounded-xl sm:overflow-hidden sm:border sm:border-border-primary">
        <div className="w-full aspect-video">
          <VideoPlayer
            video={video}
            autoplay={type === 'live'}
            onAddToCalendar={handleAddToCalendar}
            onSetReminder={handleSetReminder}
          />
        </div>
      </div>
      <div className="px-4">
        <VideoDetails
          video={video}
          onShare={handleShare}
          onSave={handleSave}
          isSaved={isSaved}
        />
      </div>
    </div>
  );
};

interface NoStreamDisplayProps {
  message: string;
}

const NoStreamDisplay: React.FC<NoStreamDisplayProps> = ({ message }) => {
  const { theme } = useTheme();
  
  return (
    <div className="w-full aspect-video bg-black sm:rounded-xl flex items-center justify-center text-white">
      <div className="text-center px-4">
        <Icon icon="ph:video-camera-slash-thin" className="w-12 h-12 mb-4 mx-auto" />
        <h3 className="text-lg font-medium">{message}</h3>
      </div>
    </div>
  );
};

const LivesPage: React.FC = () => {
  const { t } = useTranslation();
  const { liveVideo, loading: liveLoading, error: liveError } = useLiveVideo();
  const { 
    videos: upcomingVideos,
    loading: upcomingLoading
  } = useUpcomingLives({ limit: 1 });
  const { 
    videos: completedVideos, 
    loading: completedLoading,
    loadMore: loadMoreCompleted,
    error: completedError
  } = useCompletedLives({ limit: 5 });

  const nextUpcomingVideo = upcomingVideos[0];

  // SEO metadata based on current state
  const getSEOData = () => {
    if (liveVideo) {
      return {
        title: `${liveVideo.title} - ${t('common.live')}`,
        description: liveVideo.description || t('lives.description'),
        image: liveVideo.thumbnail,
        keywords: [
          ...(liveVideo.topics || []),
          'live stream',
          'live broadcast',
          'Jewish live',
          'Torah live'
        ]
      };
    }
    
    if (nextUpcomingVideo) {
      return {
        title: `${nextUpcomingVideo.title} - ${t('lives.upcoming')}`,
        description: nextUpcomingVideo.description || t('lives.description'),
        image: nextUpcomingVideo.thumbnail,
        keywords: [
          ...(nextUpcomingVideo.topics || []),
          'upcoming stream',
          'live broadcast',
          'Jewish live',
          'Torah live'
        ]
      };
    }

    return {
      title: t('navigation.live'),
      description: t('lives.description'),
      keywords: [
        'live stream',
        'live broadcast',
        'Jewish live',
        'Torah live'
      ]
    };
  };

  const seoData = getSEOData();

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-background-primary transition-colors duration-200">
        <SEO
          title={seoData.title}
          description={seoData.description}
          image={seoData.image}
          keywords={seoData.keywords}
        />
        <div className="w-full mx-auto pb-8 sm:pb-12 lg:pb-16">
          <div className="sm:p-4 sm:pb-0 max-w-[1850px] mx-auto">
            <div className="lg:flex lg:gap-6 lg:px-8 xl:px-16 2xl:px-24">
              {/* Main Content Area */}
              <div className="flex-1 mb-12 lg:mb-0">
                {liveError ? (
                  <div className="w-full aspect-video flex items-center justify-center">
                    <ErrorDisplay
                      error={liveError}
                      compact
                      onRetry={() => window.location.reload()}
                    />
                  </div>
                ) : liveLoading || upcomingLoading ? (
                  <div className="w-full aspect-video bg-black sm:rounded-xl flex items-center justify-center">
                    <LoadingSpinner size="lg" />
                  </div>
                ) : liveVideo ? (
                  <LiveStreamPlayer video={liveVideo} type="live" />
                ) : nextUpcomingVideo ? (
                  <LiveStreamPlayer video={nextUpcomingVideo} type="upcoming" />
                ) : (
                  <NoStreamDisplay message={t('lives.noStreamsAvailable')} />
                )}
              </div>

              {/* Sidebar */}
              <aside className="w-full lg:w-[400px] flex flex-col gap-12 md:gap-6">
                <UpcomingLivesContainer className="w-full" />
                
                {!completedError && (
                  <MediaListContainer
                    title={t('lives.recentlyCompleted')}
                    items={completedVideos}
                    loading={completedLoading}
                    onLoadMore={loadMoreCompleted}
                    type="video"
                  />
                )}
              </aside>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default LivesPage;